import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type Dashboard,
  type Filter,
  type Me,
  type Widget,
} from "./api";
import { BarChart } from "./widgets/BarChart";
import { Card } from "./widgets/Card";
import { DataTable } from "./widgets/DataTable";
import { DetailTable, Pivot } from "./widgets/Explore";
import { KpiChart } from "./widgets/KpiChart";
import { LineChart } from "./widgets/LineChart";
import { Clients } from "./widgets/Clients";
import { Operator } from "./widgets/Operator";
import { Search } from "./widgets/Search";

/**
 * Rendering is a switch on widget.type and nothing else.
 *
 * The handlers passed in are what make a widget drillable, but which widgets
 * exist, what they are called and how they are formatted all still come from
 * config. A second client with a different dashboard renders through this same
 * code path.
 */
function renderWidget(
  widget: Widget,
  index: number,
  handlers: {
    onPeriod: (periodStart: string) => void;
    onCategory: (dimension: string, label: string) => void;
    dimensionOf: (widget: Widget) => string | null;
  },
) {
  switch (widget.type) {
    case "card":
      return <Card key={index} widget={widget} />;
    case "line":
      return <LineChart key={index} widget={widget} onSelect={handlers.onPeriod} />;
    case "bar": {
      const dimension = handlers.dimensionOf(widget);
      return (
        <BarChart
          key={index}
          widget={widget}
          onSelect={
            dimension ? (label) => handlers.onCategory(dimension, label) : undefined
          }
        />
      );
    }
    case "table":
      return <DataTable key={index} widget={widget} />;
    default:
      return null;
  }
}

function Login({ onSuccess }: { onSuccess: (me: Me) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSuccess(await api.login(email, password));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={submit}>
        <span className="login-mark" aria-hidden="true" />
        <div className="login-word">AGL Strategy</div>

        <label>
          Email
          <input
            type="email"
            value={email}
            autoComplete="username"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function FilterBar({
  dashboard,
  filters,
  onRemove,
  onClear,
}: {
  dashboard: Dashboard;
  filters: Filter[];
  onRemove: (index: number) => void;
  onClear: () => void;
}) {
  if (filters.length === 0) return null;

  const labelFor = (filter: Filter) =>
    filter.label ??
    dashboard.dimensions.find((d) => d.field === filter.field)?.label ??
    filter.field.replace(/_/g, " ");

  return (
    <div className="filterbar">
      <span className="muted small">Filtered by</span>
      {filters.map((filter, i) => (
        <button className="chip" key={`${filter.field}-${i}`} onClick={() => onRemove(i)}>
          <span className="chip-field">{labelFor(filter)}</span>
          <span className="chip-value">{String(filter.value)}</span>
          <span className="chip-x" aria-hidden="true">
            ×
          </span>
        </button>
      ))}
      <button className="ghost small-btn" onClick={onClear}>
        Clear all
      </button>
    </div>
  );
}

/** ISO date helpers, kept string-based to avoid timezone drift on YYYY-MM-DD. */
function shiftDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86_400_000,
  );
}
function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

interface DateRange {
  from: string | null;
  to: string | null;
}

/** Date range presets + a compare-to-prior-period toggle. */
function Toolbar({
  dashboard,
  range,
  onRange,
  compare,
  onCompare,
}: {
  dashboard: Dashboard;
  range: DateRange;
  onRange: (r: DateRange) => void;
  compare: boolean;
  onCompare: (v: boolean) => void;
}) {
  if (!dashboard.period_field || !dashboard.date_max) return null;
  const max = dashboard.date_max;

  const isAll = !range.from && !range.to;
  const isMonth = range.from === monthStart(max) && range.to === max;
  const isDay = range.from === max && range.to === max;
  const bounded = Boolean(range.from && range.to);

  return (
    <div className="toolbar">
      <div className="segmented">
        <button className={isAll ? "on" : ""} onClick={() => onRange({ from: null, to: null })}>
          All time
        </button>
        <button
          className={isMonth ? "on" : ""}
          onClick={() => onRange({ from: monthStart(max), to: max })}
        >
          This month
        </button>
        <button className={isDay ? "on" : ""} onClick={() => onRange({ from: max, to: max })}>
          Latest day
        </button>
      </div>

      <div className="date-inputs">
        <input
          type="date"
          // When no range is set (All time), show the data's own bounds rather
          // than empty inputs — the filter stays "all", the boxes just aren't blank.
          value={range.from ?? dashboard.date_min ?? ""}
          min={dashboard.date_min ?? undefined}
          max={max}
          onChange={(e) => onRange({ ...range, from: e.target.value || null })}
        />
        <span className="muted">to</span>
        <input
          type="date"
          value={range.to ?? max}
          min={dashboard.date_min ?? undefined}
          max={max}
          onChange={(e) => onRange({ ...range, to: e.target.value || null })}
        />
      </div>

      <label className={`compare-toggle${bounded ? "" : " disabled"}`}>
        <input
          type="checkbox"
          checked={compare && bounded}
          disabled={!bounded}
          onChange={(e) => onCompare(e.target.checked)}
        />
        Compare to prior period
      </label>
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  const [datasets, setDatasets] = useState<string[]>([]);
  // Distinguishes "still asking" from "asked, and there is nothing" — without it
  // a tenant with no data yet sits on "Loading…" forever, because no dataset
  // means no dashboard is ever requested to finish loading.
  const [datasetsLoaded, setDatasetsLoaded] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [compare, setCompare] = useState(false);
  // Which admin surface is open, if any. null = the normal dashboard.
  const [adminView, setAdminView] = useState<"clients" | "health" | null>(null);
  const [openKpi, setOpenKpi] = useState<string | null>(null);
  const [live, setLive] = useState<Record<string, number | null> | null>(null);
  const [mtdLive, setMtdLive] = useState<Record<string, number | null> | null>(null);
  const [previous, setPrevious] = useState<Record<string, number | null> | null>(null);
  const [liveSeries, setLiveSeries] = useState<Record<string, Widget["series"]>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!me) return;
    api
      .datasets()
      .then((list) => {
        const names = list.map((d) => d.dataset);
        setDatasets(names);
        setActive((current) => current ?? names[0] ?? null);
      })
      .catch(() => setDatasets([]))
      .finally(() => setDatasetsLoaded(true));
  }, [me]);

  useEffect(() => {
    if (!active) return;
    setError(null);
    setFilters([]);
    setRange({ from: null, to: null });
    setCompare(false);
    setOpenKpi(null);
    api
      .dashboard(active)
      .then((d) => {
        setDashboard(d);
        // Open on the latest month of data with the date inputs filled, rather
        // than an empty "All time" — the current month is what a client wants to
        // see first. date_max is the newest date present, so its month is "this
        // month" for this data. Falls back to All time when there is no date field.
        setRange(
          d.date_max ? { from: monthStart(d.date_max), to: d.date_max } : { from: null, to: null },
        );
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Could not load dashboard"),
      );
  }, [active]);

  // The date range becomes filters on the period field, combined with any drill
  // filters. This is what every widget, the pivot and the detail table are
  // scoped by — one source of truth for "what am I looking at".
  const effectiveFilters = useMemo<Filter[]>(() => {
    const pf = dashboard?.period_field;
    if (!pf) return filters;
    const dateFilters: Filter[] = [];
    if (range.from) dateFilters.push({ field: pf, op: "gte", value: range.from });
    if (range.to) dateFilters.push({ field: pf, op: "lte", value: range.to });
    return [...dateFilters, ...filters];
  }, [dashboard, range, filters]);

  // The immediately preceding window of equal length, for comparison.
  const priorFilters = useMemo<Filter[] | null>(() => {
    const pf = dashboard?.period_field;
    if (!pf || !compare || !range.from || !range.to) return null;
    const length = daysBetween(range.from, range.to) + 1;
    const priorTo = shiftDays(range.from, -1);
    const priorFrom = shiftDays(priorTo, -(length - 1));
    return [
      { field: pf, op: "gte", value: priorFrom },
      { field: pf, op: "lte", value: priorTo },
      ...filters,
    ];
  }, [dashboard, compare, range, filters]);

  /**
   * When filters are active the precomputed metrics no longer describe what is
   * on screen, so EVERY widget is recomputed live — cards, time series and
   * breakdowns alike.
   *
   * Recomputing only some of them was a real bug during development: the cards
   * narrowed to one customer while the bar charts still showed all of them, so
   * the page contradicted itself. A dashboard that is partly filtered is worse
   * than one that cannot filter at all, because it looks authoritative.
   *
   * With no filters we use the precomputed values and the fast path stays fast.
   */
  useEffect(() => {
    if (!dashboard) return;
    if (effectiveFilters.length === 0) {
      // No filter and no range: the precomputed metrics table already describes
      // the whole dataset, so use it and keep the fast path fast.
      setLive(null);
      setLiveSeries({});
      return;
    }

    let cancelled = false;

    const cardMetrics = dashboard.widgets
      .filter((w) => w.type === "card")
      .flatMap((w) => w.metrics ?? []);

    if (cardMetrics.length) {
      api
        .query({ dataset: dashboard.dataset, filters: effectiveFilters, metrics: cardMetrics })
        .then((rows) => {
          if (!cancelled) setLive(rows[0]?.values ?? {});
        })
        .catch(() => !cancelled && setLive({}));
    }

    // One request per non-card widget, keyed by title so the result lands on
    // the widget it came from.
    const tasks = dashboard.widgets
      .filter((w) => w.type !== "card" && (w.metrics ?? []).length > 0)
      .map(async (widget) => {
        const metricNames = widget.metrics ?? [];
        const rows = await api.query({
          dataset: dashboard.dataset,
          filters: effectiveFilters,
          // A breakdown groups by its dimension; a time series groups by period.
          group_by: widget.dimension ?? null,
          period_type: widget.dimension ? null : (widget.period_type ?? "day"),
          metrics: metricNames,
          limit: 50,
        });
        const series: Widget["series"] = {};
        for (const name of metricNames) {
          series[name] = rows.map((r) => ({
            label: r.period_start
              ? formatPeriodLabel(r.period_start)
              : (r.group ?? "—"),
            value: r.values[name] ?? null,
            period_start: r.period_start,
          }));
        }
        return [widget.title, series] as const;
      });

    Promise.all(tasks)
      .then((results) => {
        if (!cancelled) setLiveSeries(Object.fromEntries(results));
      })
      .catch(() => !cancelled && setLiveSeries({}));

    // Filters can change faster than requests return; without this a slow
    // earlier response could overwrite a newer one.
    return () => {
      cancelled = true;
    };
  }, [dashboard, effectiveFilters]);

  // Month-to-date card values. These follow the entity filters (a customer, a
  // salesperson) but ignore the date-range toolbar — MTD always means the latest
  // month. So filtering to a customer moves the MTD figure in lockstep, while
  // sliding the date range does not.
  useEffect(() => {
    if (!dashboard || !dashboard.period_field || !dashboard.mtd_from) {
      setMtdLive(null);
      return;
    }
    let cancelled = false;
    const pf = dashboard.period_field;
    const cardMetrics = dashboard.widgets
      .filter((w) => w.type === "card")
      .flatMap((w) => w.metrics ?? []);
    const nonDate = filters.filter((f) => f.field !== pf);
    api
      .query({
        dataset: dashboard.dataset,
        filters: [
          ...nonDate,
          { field: pf, op: "gte", value: dashboard.mtd_from },
          { field: pf, op: "lte", value: dashboard.mtd_to },
        ],
        metrics: cardMetrics,
      })
      .then((rows) => !cancelled && setMtdLive(rows[0]?.values ?? {}))
      .catch(() => !cancelled && setMtdLive(null));
    return () => {
      cancelled = true;
    };
  }, [dashboard, filters]);

  // Prior-period card values, fetched only when a comparison is active.
  useEffect(() => {
    if (!dashboard || !priorFilters) {
      setPrevious(null);
      return;
    }
    let cancelled = false;
    const cardMetrics = dashboard.widgets
      .filter((w) => w.type === "card")
      .flatMap((w) => w.metrics ?? []);
    api
      .query({ dataset: dashboard.dataset, filters: priorFilters, metrics: cardMetrics })
      .then((rows) => !cancelled && setPrevious(rows[0]?.values ?? {}))
      .catch(() => !cancelled && setPrevious(null));
    return () => {
      cancelled = true;
    };
  }, [dashboard, priorFilters]);

  const addFilter = useCallback((filter: Filter) => {
    setFilters((current) => {
      // Re-drilling the same dimension replaces rather than stacks, which is
      // what clicking a second customer obviously means.
      const rest = current.filter((f) => f.field !== filter.field);
      return [...rest, filter];
    });
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setMe(null);
    setDashboard(null);
    setDatasets([]);
    setActive(null);
    setFilters([]);
  }, []);

  /** Re-read who we are, and reset everything derived from it.
   *
   * Used when entering or leaving view-as: the session now resolves to a
   * different user in a different tenant, so the dataset list, dashboard and
   * filters all have to be rebuilt rather than carried across. */
  const refreshMe = useCallback(async () => {
    setDashboard(null);
    setDatasets([]);
    setDatasetsLoaded(false);
    setActive(null);
    setFilters([]);
    setMe(await api.me());
  }, []);

  const exitViewAs = useCallback(async () => {
    await api.stopImpersonating();
    await refreshMe();
  }, [refreshMe]);

  const handlers = useMemo(
    () => ({
      onPeriod: (periodStart: string) => {
        if (!dashboard?.period_field) return;
        addFilter({
          field: dashboard.period_field,
          op: "eq" as const,
          value: periodStart,
          label: "Date",
        });
      },
      onCategory: (dimension: string, label: string) =>
        addFilter({ field: dimension, op: "eq" as const, value: label }),
      // A breakdown widget is drillable only if the dimension it charts is one
      // the config declared drillable. The dimension comes from the API rather
      // than being inferred from the widget's title.
      dimensionOf: (widget: Widget) =>
        widget.dimension &&
        dashboard?.dimensions.some((d) => d.field === widget.dimension)
          ? widget.dimension
          : null,
    }),
    [dashboard, addFilter],
  );

  if (checking) return <div className="login-shell" />;
  if (!me) return <Login onSuccess={setMe} />;

  // Overlay live values onto the configured widgets, so the layout is identical
  // whether the numbers are precomputed or freshly calculated.
  const widgets: Widget[] =
    dashboard?.widgets.map((w) => {
      // MTD cards are permanent: they always show the latest month, so the
      // toolbar range never overrides them.
      if (w.type === "card" && w.window === "mtd") return w;
      if (w.type === "card" && live) {
        const metric = (w.metrics ?? [])[0];
        return { ...w, value: metric ? (live[metric] ?? null) : null };
      }
      if (w.type !== "card" && liveSeries[w.title]) {
        return { ...w, series: liveSeries[w.title] };
      }
      return w;
    }) ?? [];

  const cards = widgets.filter((w) => w.type === "card");
  const panels = widgets.filter((w) => w.type !== "card");
  const showCompare = Boolean(previous);
  const filtering = effectiveFilters.length > 0;

  // The unfiltered company total per metric, from the original payload (before
  // any live overlay). Shown small on each card while filtering, for comparison.
  const companyByMetric: Record<string, number | null> = {};
  dashboard?.widgets
    .filter((w) => w.type === "card")
    .forEach((w) => {
      companyByMetric[(w.metrics ?? [])[0]] = w.value;
    });

  const periodLabel =
    !range.from && !range.to
      ? "All time"
      : range.from === range.to
        ? range.from
        : `${range.from ?? "start"} – ${range.to ?? "latest"}`;

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          <div>
            <div className="tenant">{me.tenant_name}</div>
            <div className="muted small">{me.email}</div>
          </div>
        </div>
        <div className="topbar-right">
          {/* Admin surfaces follow the REAL user, so they stay reachable while
              viewing as a client — that is where the exit control lives. */}
          {me.is_operator && !me.impersonating && (
            <>
              <button
                className={adminView === "clients" ? "ghost on" : "ghost"}
                onClick={() => setAdminView(adminView === "clients" ? null : "clients")}
              >
                Clients
              </button>
              <button
                className={adminView === "health" ? "ghost on" : "ghost"}
                onClick={() => setAdminView(adminView === "health" ? null : "health")}
              >
                Operations
              </button>
            </>
          )}
          {!adminView && datasets.length > 1 && (
            <select value={active ?? ""} onChange={(e) => setActive(e.target.value)}>
              {datasets.map((d) => (
                <option key={d} value={d}>
                  {d.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          )}
          <button className="ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {me.impersonating && (
        <div className="impersonation-banner">
          <span>
            Viewing as <strong>{me.impersonating}</strong> — read-only
          </span>
          <button className="ghost small-btn" onClick={exitViewAs}>
            Exit
          </button>
        </div>
      )}

      <main>
        {adminView === "health" && <Operator />}
        {adminView === "clients" && (
          <Clients onImpersonated={() => { setAdminView(null); refreshMe(); }} />
        )}
        {!adminView && (
        <>
        {error && <div className="error banner">{error}</div>}
        {!dashboard && !error && datasetsLoaded && datasets.length === 0 && (
          <div className="empty">
            <strong>No data yet.</strong>
            <div className="muted small" style={{ marginTop: 6 }}>
              This account is set up, but no files have been loaded. Data appears
              here once the first file is processed.
            </div>
          </div>
        )}
        {!dashboard && !error && !(datasetsLoaded && datasets.length === 0) && (
          <div className="empty">Loading…</div>
        )}

        {dashboard && (
          <div className="controls-row">
            <Search dashboard={dashboard} onSelect={addFilter} />
            <Toolbar
              dashboard={dashboard}
              range={range}
              onRange={setRange}
              compare={compare}
              onCompare={setCompare}
            />
          </div>
        )}

        {dashboard && (
          <FilterBar
            dashboard={dashboard}
            filters={filters}
            onRemove={(i) => setFilters((f) => f.filter((_, index) => index !== i))}
            onClear={() => setFilters([])}
          />
        )}

        {cards.length > 0 && (
          <>
            <div className="cards-caption">
              <span className="muted small">KPIs for</span> <strong>{periodLabel}</strong>
              <span className="muted small"> · MTD and company totals shown for comparison</span>
            </div>
            <section className="cards">
              {cards.map((w, i) => {
                const metric = (w.metrics ?? [])[0];
                return (
                  <Card
                    key={i}
                    widget={w}
                    mtd={mtdLive?.[metric] ?? null}
                    company={filtering ? (companyByMetric[metric] ?? null) : null}
                    previous={showCompare ? (previous?.[metric] ?? null) : undefined}
                    active={openKpi === metric}
                    onClick={() => setOpenKpi((cur) => (cur === metric ? null : metric))}
                  />
                );
              })}
            </section>

            {openKpi &&
              (() => {
                const w = cards.find((c) => (c.metrics ?? [])[0] === openKpi);
                if (!w || !dashboard) return null;
                return (
                  <KpiChart
                    dashboard={dashboard}
                    metric={openKpi}
                    label={w.title}
                    format={w.format}
                    filters={effectiveFilters}
                    onClose={() => setOpenKpi(null)}
                  />
                );
              })()}
          </>
        )}

        {dashboard && (
          <section className="panels">
            <Pivot dashboard={dashboard} filters={effectiveFilters} onDrill={addFilter} />
            {panels.map((w, i) => renderWidget(w, i, handlers))}
            <DetailTable
              dataset={dashboard.dataset}
              filters={effectiveFilters}
              sortField={dashboard.period_field}
            />
          </section>
        )}
        </>
        )}
      </main>
    </div>
  );
}

function formatPeriodLabel(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}
